import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPatch } from '../lib/api';
import { congesErrorMessage } from '../lib/conges';
import { useFeature } from '../context/AuthContext';

interface CongeRow {
  id_user: number;
  nom_user: string;
  id_user_traitant_conges: number;
  substitute_nom: string | null;
}

export function AdminCongesPage() {
  const canWrite = useFeature('admin_accounts');
  const [rows, setRows] = useState<CongeRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState<number | null>(null);

  const load = () => {
    setErr(null);
    apiGet<{ items: CongeRow[] }>('/api/admin/conges')
      .then((d) => setRows(d.items))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Erreur'));
  };

  useEffect(() => {
    load();
  }, []);

  async function setSubstitute(idUser: number, idSub: number) {
    setSaving(idUser);
    try {
      await apiPatch(`/api/admin/conges/${idUser}`, { id_substitute: idSub });
      load();
    } catch (e) {
      setErr(congesErrorMessage(e));
    } finally {
      setSaving(null);
    }
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/" style={{ fontSize: 'var(--fs-xs)' }}>
          ← retour
        </Link>
      </div>
      <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, marginBottom: 8 }}>🌴 Substitution congés</h1>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', marginBottom: 16 }}>
        Quand un traitant a un substitut configuré, les nouveaux points qui lui auraient été assignés
        automatiquement sont redirigés vers son substitut. Mettre <em>—</em> pour annuler.
      </p>
      {err && (
        <div role="alert" style={{ color: 'var(--danger)', marginBottom: 12, fontSize: 'var(--fs-sm)', padding: 10, background: 'var(--danger-soft)', borderRadius: 6 }}>
          {err}
        </div>
      )}
      <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8 }}>
        {rows.map((r, i) => (
          <div
            key={r.id_user}
            className="grid-2-actions"
            style={{
              gap: 12,
              padding: '10px 14px',
              borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
              fontSize: 'var(--fs-sm)',
            }}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{r.nom_user}</div>
              <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>id #{r.id_user}</div>
            </div>
            <select
              value={r.id_user_traitant_conges}
              disabled={!canWrite || saving === r.id_user}
              onChange={(e) => setSubstitute(r.id_user, Number(e.target.value))}
              style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4 }}
            >
              <option value={0}>— Aucun —</option>
              {/* Substitut posé hors de la V2 et qui n'est pas un agent Digital
                  Team : affiché tel quel (pour pouvoir le retirer), non proposé. */}
              {r.id_user_traitant_conges > 0 && !rows.some((x) => x.id_user === r.id_user_traitant_conges) && (
                <option value={r.id_user_traitant_conges} disabled>
                  {r.substitute_nom ?? `#${r.id_user_traitant_conges}`} (hors Digital Team)
                </option>
              )}
              {rows
                .filter((x) => x.id_user !== r.id_user)
                .map((x) => (
                  <option key={x.id_user} value={x.id_user}>
                    {x.nom_user}
                  </option>
                ))}
            </select>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
              {saving === r.id_user ? '…' : r.id_user_traitant_conges > 0 ? '✓ actif' : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
